import { useEffect, useCallback } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { Flex, View } from "@adobe/react-spectrum";
import {
  CALL_STATES,
  CALL,
  CALLBACK,
  CHAT,
  CASE,
  NONE,
  SETTINGS,
  CHANNEL_TYPES,
  HELPSUPPORT,
  SPEED_DIAL,
  MY_NOTES,
} from "../../constants/constants";
import "./style.css";
import {
  callTabs,
  callbackTabs,
  voicemailTabs,
  settingsTabs,
  caseTabs,
  chatTabs,
  helpSupportTabs,
} from "../common/Data/data";
import SidebarCP from "../Sidebar";
import storeRTK from "../../store/storeRTK";
import { setCurrentSessionTab } from "../../actions/session";
import { RootState } from "../../store/storeConfig";
import { tabAccessControl } from "../../utils/utils";
import HeaderComponent from "../Login/HeaderComponent";
import TabSwitch from "../common/Tabs/TabSwitch";
import CaseAiDrawer from "../CasePage/CaseAI/CaseAiDrawer";
import SessionTimer from "../SessionTimer";
import SpeedDial from "../SpeedDial/SpeedDial";
import MyNotes from "../MyNotes/MyNotes";
import GlobalBanner from "../common/GlobalBanner";

const Dashboard = () => {
  const { agent, history, call, callback, chat, cases, voicemail, session } =
    useSelector(
      (state: RootState) => ({
        agent: state.agent,
        history: state.history,
        call: state.call,
        callback: state.callback,
        chat: state.chat,
        cases: state.cases,
        voicemail: state.voicemail,
        session: state.session,
      }),
      shallowEqual
    );

  const handleTabChange = useCallback((id: string) => {
    storeRTK.dispatch(setCurrentSessionTab({ id }));
  }, []);

  const updatedCallTabs = callTabs.map((tab) =>
    tab.name === "Monitoring"
      ? { ...tab, active: tabAccessControl("contact_monitor") }
      : tab
  );

  const updatedCallbackTabs = callbackTabs.map((tab) =>
    tab.name === "Callback Dashboard"
      ? { ...tab, active: tabAccessControl("callback_dashboard") }
      : tab
  );

  const checkAndSwitchTab = useCallback(
    (condition: boolean, tab: string) =>
      condition && session.currentTab !== tab && handleTabChange(tab),
    [session.currentTab, handleTabChange]
  );

  useEffect(() => {
    checkAndSwitchTab(
      call.id && call.self?.state === CALL_STATES.RINGING,
      CALL
    );
  }, [call.id, call.self?.state, checkAndSwitchTab]);

  useEffect(() => {
    checkAndSwitchTab(
      callback.id && callback.self?.callback?.state === CALL_STATES.RINGING,
      CALLBACK
    );
  }, [callback.id, callback.self?.callback?.state, checkAndSwitchTab]);

  useEffect(() => {
    checkAndSwitchTab(
      voicemail.id && voicemail.self?.voicemail?.state === CALL_STATES.RINGING,
      CHANNEL_TYPES.VOICEMAIL
    );
  }, [voicemail.id, voicemail.self?.voicemail?.state, checkAndSwitchTab]);

  useEffect(() => {
    checkAndSwitchTab(
      !chat.activeChatID &&
      chat.chats?.length > 0 &&
      session.currentTab === NONE,
      CHAT
    );
  }, [chat.activeChatID, chat.chats, session.currentTab, checkAndSwitchTab]);

  useEffect(() => {
    checkAndSwitchTab(
      !cases.activeCaseID &&
      cases.cases?.length > 0 &&
      session.currentTab === NONE,
      CASE
    );
  }, [cases.activeCaseID, cases.cases, session.currentTab, checkAndSwitchTab]);

  return (
    <View UNSAFE_className="mainWrapper">
      <HeaderComponent />
      <GlobalBanner />
      <Flex direction="row" width="100%" UNSAFE_className="aic-body">
        <View width="13.3%">
          <SidebarCP />
        </View>
        <View width="88%" height="100%" backgroundColor="static-white">
          {session.currentTab === CALL && (
            <TabSwitch
              tabs={updatedCallTabs.filter((t) => t.active === "true")}
              history={history.conversations}
              isActiveChannel={call?.id}
            />
          )}
          {session.currentTab === CASE && (
            <TabSwitch
              tabs={caseTabs}
              history={history.cases}
              isActiveChannel={cases?.activeCaseID}
            />
          )}
          {session.currentTab === CALLBACK && (
            <TabSwitch
              tabs={updatedCallbackTabs.filter((t) => t.active === "true")}
              history={history.callback}
              isActiveChannel={callback?.id}
            />
          )}
          {session.currentTab === CHANNEL_TYPES.VOICEMAIL && (
            <TabSwitch
              tabs={voicemailTabs.filter((t) => t.active === "true")}
              history={history.voicemail}
              isActiveChannel={voicemail?.id}
            />
          )}
          {session.currentTab === CHAT && (
            <TabSwitch
              tabs={chatTabs.filter((t) => t.active === "true")}
              history={history.chat}
              isActiveChannel={chat?.id}
            />
          )}
          {session.currentTab === SETTINGS && (
            <TabSwitch tabs={settingsTabs.filter((t) => t.active === "true")} />
          )}
          {session.currentTab === HELPSUPPORT && (
            <TabSwitch
              tabs={helpSupportTabs.filter((t) => t.active === "true")}
            />
          )}
          {session.currentTab === SPEED_DIAL && <SpeedDial />}
          {session.currentTab === MY_NOTES && <MyNotes />}
        </View>
      </Flex>
      {(agent.aicConfig?.ask_ai_enabled === "true" ||
        agent.aicConfig?.ask_ai_enabled === true) && <CaseAiDrawer />}
      {session.loggedIn && <SessionTimer />}
    </View>
  );
};

export default Dashboard;
